/* ==========================================================================
   NABAT — deep-link gallery alignment        (nabat-gallery-deeplink.js)

   THE DEFECT (audit C1)
   product-media-gallery.liquid resolves product.selected_variant
   .featured_media.id and moves `is-active` onto the matching <li> and
   thumbnail. But the gallery is a horizontal scroll-snap track at every
   breakpoint, `.media-gallery__item.is-active` carries no visual
   declaration of its own, and nothing scrolls the track on load —
   media-gallery.js is byte-identical to the live theme and its sync line
   is still commented out. So landing on /products/x?variant=Y painted
   photo #1 with thumbnail #3 highlighted: the right thumbnail, the wrong
   photo.

   WHAT THIS DOES
   Once, on DOMContentLoaded, and ONLY when the URL actually carries a
   `variant=` parameter, it brings the already-marked active slide into
   view inside its own gallery track. Nothing else on the page moves.

   WHY IT CANNOT DO HARM
   - Gated on `variant=`: with no deep link the function returns before it
     touches the DOM, so the ordinary product page is byte-for-byte the
     page it is today.
   - Everything is inside try/catch, and every object is existence-checked
     before use, so a missing gallery, a missing element or an engine that
     rejects the options object all end as a silent no-op — never a thrown
     error, never a broken buy button.
   - `block: 'nearest'` means the page does not scroll vertically when the
     gallery is already in view, which on first paint it is.
   - `behavior: 'instant'` — no animation, so there is nothing for
     prefers-reduced-motion to suppress. The reduced-motion branch below is
     belt and braces for engines that read `behavior` from CSS instead.
   - It runs once and never re-binds, so product-info.js re-rendering the
     gallery on a variant click is completely unaffected — that path still
     belongs to the theme's own setActiveMedia().
   - `inline: 'center'` is a logical value, so the Arabic RTL page mirrors
     correctly without a second code path.
   ========================================================================== */
(function () {
  'use strict';

  var ITEM = '.media-gallery__item.is-active';
  var TRACK = '.media-gallery__list';

  function alignDeepLinkedSlide() {
    try {
      /* 1. no deep link, no work. */
      if (typeof window === 'undefined' || !window.location) return;
      var search = window.location.search;
      if (!search || search.indexOf('variant=') === -1) return;

      if (typeof document === 'undefined' || !document.querySelectorAll) return;

      var items = document.querySelectorAll(ITEM);
      if (!items || !items.length) return;

      var reduce = false;
      try {
        reduce = !!(window.matchMedia &&
                    window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      } catch (mqError) {
        reduce = false;
      }

      for (var i = 0; i < items.length; i++) {
        var item = items[i];
        if (!item || typeof item.closest !== 'function') continue;

        /* 2. only ever scroll a real gallery track. */
        var track = item.closest(TRACK);
        if (!track) continue;

        /* 3. nothing to correct unless the track can actually scroll. */
        if (track.scrollWidth <= track.clientWidth + 1) continue;

        if (typeof item.scrollIntoView !== 'function') continue;

        try {
          item.scrollIntoView({
            block: 'nearest',
            inline: 'center',
            behavior: reduce ? 'auto' : 'instant'
          });
        } catch (optionsError) {
          /* Older engines throw on an unrecognised `behavior` value.
             Retry without it — still instant unless the theme has asked
             for smooth scrolling in CSS. */
          try {
            item.scrollIntoView({ block: 'nearest', inline: 'center' });
          } catch (retryError) {
            /* give up quietly: the page keeps today's behaviour */
          }
        }
      }
    } catch (error) {
      /* never throw */
    }
  }

  try {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', alignDeepLinkedSlide, { once: true });
    } else {
      alignDeepLinkedSlide();
    }
  } catch (bindError) {
    /* `{ once: true }` is refused by engines without options support */
    try {
      document.addEventListener('DOMContentLoaded', alignDeepLinkedSlide);
    } catch (finalError) {
      /* no-op */
    }
  }
})();
